import { useHistory } from 'react-router';
import { ActionMenu } from 'ui/actionMenu';
import {
  ActionMenuOption,
  AllowedActionMenuElements,
  MENU_ALIGNMENTS,
  MENU_DIRECTIONS,
} from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Delete24 } from 'ui/icons/aurora/24px/Delete24';
import { Handlebars24 } from 'ui/icons/aurora/24px/Handlebars24';
import { MakeCopy24 } from 'ui/icons/aurora/24px/MakeCopy24';
import { Overflow16 } from 'ui/icons/aurora/16px/Overflow16';
import { Overflow24 } from 'ui/icons/aurora/24px/Overflow24';
import { colors } from 'ui/theme/colors';

import { GraphQLSnippet } from '../types';
import { useSnippetQueryContext } from '../context/SnippetQueryContext';
import { useSnippetModals } from '../hooks/useSnippetModals';
import { useTranslations } from '../hooks/useTranslations';

type Props = {
  snippet: GraphQLSnippet;
  direction?: MENU_DIRECTIONS;
  smallButton?: boolean;
};

export const SnippetOverflowMenu = ({
  snippet,
  direction = MENU_DIRECTIONS.BOTTOM,
  smallButton = false,
  ...otherProps
}: Props) => {
  const { t } = useTranslations();
  const { setSelectedSnippet } = useSnippetQueryContext();

  const modals = useSnippetModals();
  const history = useHistory();

  const menuOptions: ActionMenuOption[] = [
    {
      id: 'make-a-copy',
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <MakeCopy24 />
          <span>{t('menu.copy')}</span>
        </>
      ),
      onClick: () => {
        modals.copy.toggle(true);
        setSelectedSnippet(snippet);
      },
    },
    {
      id: 'usage-details',
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Handlebars24 />
          <span>{t('menu.usage')}</span>
        </>
      ),
      onClick: () => {
        history.push(`/templates/snippet/${snippet.id}/locations`, {
          prevUrl: history.location.pathname,
        });
      },
    },
    {
      id: 'delete',
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Delete24 />
          <span>{t('menu.delete')}</span>
        </>
      ),
      onClick: () => {
        modals.delete.toggle(true);
        setSelectedSnippet(snippet);
      },
    },
  ];

  return (
    <ActionMenu
      align={MENU_ALIGNMENTS.RIGHT}
      direction={direction}
      id={`action-menu-${snippet.id}`}
      options={menuOptions}
      {...otherProps}
    >
      {smallButton ? (
        <IconButton variant={BUTTON_VARIANTS.SECONDARY} size={BUTTON_SIZES.SMALL}>
          <Overflow16 color={colors.iconPrimary} />
        </IconButton>
      ) : (
        <IconButton variant={BUTTON_VARIANTS.SECONDARY} size={BUTTON_SIZES.LARGE}>
          <Overflow24 color={colors.iconPrimary} />
        </IconButton>
      )}
    </ActionMenu>
  );
};
